import styled from "@emotion/styled";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMediaQuery, Tooltip, IconButton } from "@mui/material";
import { ExpertProfileForm } from "@src/api/types";
import { EmptySpace } from "@src/component/SignComponent";
import { GroupA } from "@src/ruix";
import { numberWithCommasFromNumber } from "@src/utils/CalculationUtils";
import Linkify from "linkify-react";
import { ReactNode } from "react";

interface ComponentProps {
	data: Partial<ExpertProfileForm>;
	showOpenPrice?: boolean;
	isRecommended?: boolean;
	aiSummarySlot?: ReactNode;
}

const BEST_EXPERT_2024_NICKNAMES = [
	"용지니",
	"hidy",
	"key",
	"Zoe B",
	"nry",
	"유진",
	"루시",
	"에드",
	"seanine",
	"C2",
	"M-J",
	"허프",
	"데이",
	"해인",
	"범쌤",
	"손희수",
	"날뛰는캥거루",
	"엠마",
	"필립",
	"연수이",
];

// 2025 Best Expert 닉네임 목록
const BEST_EXPERT_2025_NICKNAMES = [
	"hidy",
	"AmandaLee",
	"스카이",
	"올라운더해나",
	"강제인",
	"sjsj",
	"hani",
	"조조",
	"nomadbellaaa",
	"Zoe B",
	"소정",
	"tami",
	"key",
	"댄디라이언",
	"유진",
	"지구인",
	"리드젠 엑스퍼트",
	"nry",
	"스텔라송",
	"TAM",
	"용지니",
];

export const Section1 = ({ data, showOpenPrice, isRecommended, aiSummarySlot }: ComponentProps) => {
	const isMobile = useMediaQuery("(max-width:700px)");
	const isBestExpert2025 = data.name && BEST_EXPERT_2025_NICKNAMES.includes(data.name);
	const isBestExpert2024 = data.name && BEST_EXPERT_2024_NICKNAMES.includes(data.name);

	return (
		<Wrapper>
			<div className="top">
				<div className="top-title-box">
					<div className="top-title-with-badge">
						<div className="top-title">{data.name}</div>
						{isBestExpert2024 && (
							<div className="badge-container">
								<img
									src="/onepoint/assets/images/icon/2024_bey.svg"
									alt="2024 Best Expert"
									className="best-expert-badge"
								/>
								<Tooltip
									title="2024년 최고의 전문가 19인에 선정된 전문가입니다!"
									placement="top"
									arrow
								>
									<IconButton className="badge-info-button">
										<InfoOutlinedIcon className="badge-info-icon" />
									</IconButton>
								</Tooltip>
							</div>
						)}
						{isBestExpert2025 && (
							<div className="badge-container">
								<img
									src="/onepoint/assets/images/icon/2025_bey.svg"
									alt="2025 Best Expert"
									className="best-expert-badge"
								/>
								<Tooltip
									title="2025년 최고의 전문가 20인에 선정된 전문가입니다!"
									placement="top"
									arrow
								>
									<IconButton className="badge-info-button">
										<InfoOutlinedIcon className="badge-info-icon" />
									</IconButton>
								</Tooltip>
							</div>
						)}
					</div>
					<div className="toptier-box">
						<div className="toptier">
							<VerifiedIcon sx={{ fill: "var(--primary)" }} />
							<a
								href="https://notion.1point.kr/toptier"
								target="_blank"
								rel="noreferrer"
							>
								원포인트 검증 전문가
							</a>
						</div>
						<EmptySpace space={10} />
						{isRecommended ? (
							<div className="toptier">
								<ThumbUpOutlinedIcon sx={{ fill: "var(--primary)" }} />
								<a
									href="https://notion.1point.kr/toptier"
									target="_blank"
									rel="noreferrer"
								>
									원포인트 추천 전문가
								</a>
							</div>
						) : (
							<></>
						)}
					</div>
				</div>
				<div className="top-text">
					{data.job} {data.year ? data.year + "년차" : ""}
				</div>
				{aiSummarySlot ? (
					<>
						<EmptySpace space={14} />
						<div className="ai-summary-slot">{aiSummarySlot}</div>
					</>
				) : null}
			</div>
			{data.briefDescription && data.briefDescription.data != "" && (
				<div className="intro">
					<div id="intro-data">
						<Linkify
							as="span"
							options={{
								className: "linkify",
								target: "_blank",
							}}
						>
							{data.briefDescription.data}
						</Linkify>
					</div>
				</div>
			)}
			<EmptySpace space={30} />
			<GroupA fullWidth order="row-start-start" gap={30}>
				{showOpenPrice && data.openPrice ? (
					<GroupA order="column-start-start">
						<Title>시간당 금액</Title>
						<SubTitle>{numberWithCommasFromNumber(data.openPrice)}원/1시간</SubTitle>
					</GroupA>
				) : null}
				{data.year && (
					<GroupA order="column-start-start">
						<Title>연차</Title>
						<SubTitle>{data.year}년차</SubTitle>
					</GroupA>
				)}
				<GroupA order="column-start-start">
					<Title>현 상태</Title>
					<SubTitle>{data.currentStatus}</SubTitle>
				</GroupA>
				<GroupA order="column-start-start">
					<Title>업무 가능 시간</Title>
					<SubTitle>{data.jobTime}</SubTitle>
				</GroupA>
			</GroupA>
		</Wrapper>
	);
};

const Title = styled.div`
	color: var(--desc-gray);
	font-size: 16px;
	font-weight: 500;
	line-height: normal;
	letter-spacing: -0.64px;
	line-height: 150%; /* 24px */
`;

const SubTitle = styled.div`
	color: var(--card-bg);
	font-size: 16px;
	font-style: normal;
	font-weight: 700;
	line-height: 150%; /* 24px */
	letter-spacing: -0.64px;
`;

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;

	.contents {
		color: var(--card-bg);
		display: flex;
	}

	.top {
		display: flex;
		flex-direction: column;
		width: 100%;

		.ai-summary-slot {
			width: 100%;
		}

		.top-title-box {
			display: flex;
			flex-direction: row;
			width: 100%;
			align-items: center;
			justify-content: space-between;

			.top-title-with-badge {
				display: flex;
				flex-direction: row;
				align-items: center;

				.badge-container {
					position: relative;
					display: inline-block;

					.best-expert-badge {
						width: 75px;
						height: 75px;
						flex-shrink: 0;
						display: block;
						animation: badge-appear 0.5s ease-out;
					}

					.badge-info-button {
						position: absolute;
						top: 7px;
						right: 7px;
						padding: 3px;
						background-color: transparent;

						&:hover {
							background-color: rgba(255, 255, 255, 0.3);
						}

						.badge-info-icon {
							width: 11px;
							height: 11px;
							color: var(--primary);
						}
					}
				}

				@keyframes badge-appear {
					from {
						opacity: 0;
						transform: scale(0.8);
					}
					to {
						opacity: 1;
						transform: scale(1);
					}
				}
			}

			.toptier {
				display: flex;
				flex-direction: row;
				gap: 4px;
				align-items: center;
				a {
					color: var(--primary);
					font-size: 16px;
					font-weight: 600;
					line-height: normal;
					text-decoration: underline;
				}
			}
		}

		@media screen and (max-width: 768px) {
			.top-title-box {
				display: flex;
				flex-direction: column;
				width: 100%;
				align-items: flex-start;
				gap: 8px;
				margin-bottom: 20px;

				.top-title-with-badge {
					.best-expert-badge {
						width: 100px;
						height: 100px;
					}
				}
			}
		}

		.top-title {
			color: var(--card-bg);
			font-size: 35px;
			font-weight: 800;
			line-height: normal;
		}

		.top-text {
			margin-top: 8px;
			color: var(--card-bg);
			font-size: 20px;
			font-weight: 600;
			line-height: normal;
		}

		.logo {
			width: 50px;
			height: 50px;
			margin-left: 16px;
		}
	}

	.intro {
		margin-top: 18px;
		width: 100%;

		#intro-title {
			color: var(--desc-gray);
			font-size: 20px;
			font-weight: 400;
			line-height: normal;
		}

		#intro-data {
			color: var(--desc-gray);
			font-size: 16px;
			font-weight: 400;
			margin-top: 14px;
			line-height: 28px;

			.linkify {
				color: var(--primary);
				cursor: pointer;
			}

			a {
				text-decoration: underline;
			}
		}
	}
`;
